/**
 * bvaPick · «выбери границы» 3-value BVA picker (S05 MGT-03 · 10 JUN 2026).
 *
 * The student clicks value-chips to assemble the correct 3-value boundary set
 * for a spec, then checks: correct picks turn green, extra picks red, missed
 * boundaries show as dashed outlines — the gap between «выучил правило» and
 * «применил правило» становится видимым за один клик. Round 2 teaches the
 * «сосед границы = ±шаг» trap (0.99/1.01, не 0/2).
 *
 * Markup contract (authored · no-JS degradable — without this script the
 * correct chips are dash-highlighted and the success razor shows via CSS):
 *   <div class="bva">
 *     <div class="bva-head"><span class="t">…</span>
 *       <span class="bva-cnt">выбрано <b class="bva-num">0</b></span></div>
 *     <div class="bva-spec">…спецификация…</div>
 *     <span class="bva-rule">правило · …</span>
 *     <div class="bva-chips">
 *       <button type="button" class="bva-chip" data-k="b">1</button>   ← граница (надо выбрать)
 *       <button type="button" class="bva-chip" data-k="x">4</button>   ← ловушка
 *     </div>
 *     <div class="bva-act"><button type="button" class="bva-check" disabled>Проверить</button></div>
 *     <div class="bva-fb" data-good="…" data-bad="…"></div>
 *   </div>
 *
 * StrictMode-safe: authored DOM not destroyed; cleanup removes listeners,
 * state classes and the init guard.
 */

export function initBvaPick(root: HTMLElement): () => void {
  if (typeof document === 'undefined') return () => {};
  const cleanups: Array<() => void> = [];

  const bvas = Array.from(root.querySelectorAll<HTMLElement>('.bva'));
  for (const bva of bvas) {
    if (bva.dataset.bvaInit === '1') continue;
    bva.dataset.bvaInit = '1';
    bva.classList.add('bva-ready');

    const chips = Array.from(bva.querySelectorAll<HTMLButtonElement>('.bva-chip'));
    const check = bva.querySelector<HTMLButtonElement>('.bva-check');
    const fb = bva.querySelector<HTMLElement>('.bva-fb');
    const num = bva.querySelector<HTMLElement>('.bva-num');
    if (chips.length === 0 || !check || !fb) continue;

    let picked = 0;
    let done = false;
    const handlers: Array<[HTMLElement, EventListener]> = [];

    const on = (el: HTMLElement, fn: EventListener) => {
      el.addEventListener('click', fn);
      handlers.push([el, fn]);
    };

    for (const chip of chips) {
      on(chip, () => {
        if (done) return;
        chip.classList.toggle('pick');
        picked += chip.classList.contains('pick') ? 1 : -1;
        if (num) num.textContent = String(picked);
        check.disabled = picked === 0;
      });
    }

    on(check, () => {
      if (done) return;
      done = true;
      let allOk = true;
      for (const chip of chips) {
        chip.disabled = true;
        const isB = chip.dataset.k === 'b';
        const isPick = chip.classList.contains('pick');
        chip.classList.remove('pick');
        if (isB && isPick) chip.classList.add('right');
        else if (!isB && isPick) {
          chip.classList.add('extra');
          allOk = false;
        } else if (isB && !isPick) {
          chip.classList.add('missed');
          allOk = false;
        }
      }
      check.style.display = 'none';
      fb.classList.add('show', allOk ? 'good' : 'bad');
      fb.textContent = (allOk ? fb.dataset.good : fb.dataset.bad) ?? '';
    });

    cleanups.push(() => {
      handlers.forEach(([el, fn]) => el.removeEventListener('click', fn));
      chips.forEach((c) => {
        c.disabled = false;
        c.classList.remove('pick', 'right', 'extra', 'missed');
      });
      check.disabled = true;
      check.style.removeProperty('display');
      fb.classList.remove('show', 'good', 'bad');
      fb.textContent = '';
      if (num) num.textContent = '0';
      bva.classList.remove('bva-ready');
      delete bva.dataset.bvaInit;
    });
  }

  return () => cleanups.forEach((fn) => fn());
}
